import type { VirtualBackgroundProcessor } from '@twilio/video-processors';
import { TypedEmitter } from 'tiny-typed-emitter';
import { LocalVideoTrack, VideoProcessor } from 'twilio-video';
import { saveBackgroundId, getSavedBackgroundId } from './backgroundPreferences';

const ASSETS_PATH = '/static/video-processors';

export const NO_BACKGROUND = 'none';

export interface VideoBackground {
  id: string;
  label: string;
  src: string;
}

export const videoBackgrounds: VideoBackground[] = [
  { id: 'hbs-light', label: 'HBS Light', src: '/static/images/videoBackgrounds/hbs-light.jpg' },
  { id: 'hbs-orange', label: 'HBS Orange', src: '/static/images/videoBackgrounds/hbs-orange.jpg' },
  { id: 'hbs-blue', label: 'HBS Blue', src: '/static/images/videoBackgrounds/hbs-blue.jpg' }
];

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load background image ${src}`));
    image.src = src;
  });

interface MessageEvents {
  changed: (backgroundId: string) => void;
  loadingChanged: (isLoading: boolean) => void;
}

class VideoBackgroundManager extends TypedEmitter<MessageEvents> {
  private processor?: VirtualBackgroundProcessor;
  private track?: LocalVideoTrack;
  private _backgroundId = NO_BACKGROUND;
  private _isLoading = false;

  get backgroundId() {
    return this._backgroundId;
  }

  get isLoading() {
    return this._isLoading;
  }

  private setLoading(isLoading: boolean) {
    this._isLoading = isLoading;
    this.emit('loadingChanged', isLoading);
  }

  async attachTo(track: LocalVideoTrack) {
    if (this.track === track) return;

    this.removeProcessor();
    this.track = track;

    if (this._backgroundId !== NO_BACKGROUND) {
      await this.applyBackground(this._backgroundId);
    }
  }

  detach() {
    this.removeProcessor();
    this.track = undefined;
  }

  suspend() {
    this.removeProcessor();
  }

  resume() {
    if (this._backgroundId === NO_BACKGROUND) return;
    this.addProcessor();
  }

  async setBackground(backgroundId: string) {
    this._backgroundId = backgroundId;
    saveBackgroundId(backgroundId);

    if (backgroundId === NO_BACKGROUND) {
      this.removeProcessor();
    } else {
      await this.applyBackground(backgroundId);
    }

    this.emit('changed', backgroundId);
  }

  restoreSaved() {
    const saved = getSavedBackgroundId();
    if (!saved || saved === NO_BACKGROUND) return Promise.resolve();
    if (!videoBackgrounds.some(({ id }) => id === saved)) return Promise.resolve();

    return this.setBackground(saved);
  }

  private async applyBackground(backgroundId: string) {
    const background = videoBackgrounds.find(({ id }) => id === backgroundId);
    if (!background || !this.track) return;

    this.setLoading(true);

    try {
      const image = await loadImage(background.src);

      if (this.processor) {
        this.processor.backgroundImage = image;
      } else {
        const { VirtualBackgroundProcessor } = await import('@twilio/video-processors');
        this.processor = new VirtualBackgroundProcessor({ assetsPath: ASSETS_PATH, backgroundImage: image });
        await this.processor.loadModel();
      }

      this.addProcessor();
    } catch (error) {
      console.error('[videoBackground] Failed to apply background', error);
    } finally {
      this.setLoading(false);
    }
  }

  private addProcessor() {
    if (!this.track || !this.processor) return;
    if (this.track.processor) return;

    this.track.addProcessor(this.processor as unknown as VideoProcessor, {
      inputFrameBufferType: 'videoframe',
      outputFrameBufferContextType: 'bitmaprenderer'
    });
  }

  private removeProcessor() {
    if (!this.track?.processor) return;

    this.track.removeProcessor(this.track.processor);
  }
}

export const videoBackground = new VideoBackgroundManager();
